import { generatePath, Link } from 'react-router-dom'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { useModals } from 'almanac-ui/Modal'
import { BRAND_PLAN_SCHEDULE_PATH } from 'paths'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ContentStrategyTypeLabel } from '../../../../constants'
import { EmojiMap } from 'components/ContentStrategyTypeEmoji/ContentStrategyTypeEmoji'
import { CampaignInfo, ContentStrategyType } from 'types'
import { MoveToCampaignModal } from './MoveToCampaignModal/MoveToCampaignModal'
import Styles from './CampaignSection.module.scss'

interface CampaignSectionProps {
  campaignInfo: CampaignInfo
  contentId?: number
  fileKey?: string
  onClose?: () => void
}

const getStrategyLabel = (strategyType: ContentStrategyType | null): string | null => {
  if (!strategyType) return null
  const emoji = EmojiMap[strategyType] || ''
  const label = ContentStrategyTypeLabel[strategyType] || ''
  if (!label) return null
  return `${emoji} ${label}`.trim()
}

export const CampaignSection = ({ campaignInfo, contentId, fileKey, onClose }: CampaignSectionProps) => {
  const workspaceId = useCurrentWorkspaceId()
  const { openModal } = useModals()

  const hasNoCampaign = campaignInfo.is_one_off || !campaignInfo.title

  const canChangeCampaign = campaignInfo.is_one_off && contentId !== undefined && !!fileKey

  const handleChange = () => {
    if (contentId === undefined || !fileKey) return

    openModal(
      MoveToCampaignModal,
      {
        brandPlanId: campaignInfo.brand_plan_id,
        sourceScheduleId: campaignInfo.id,
        contentId,
        fileKey,
      },
      { newStack: true },
    )
  }

  if (hasNoCampaign) {
    return (
      <div className={`${Styles.card} ${Styles.cardStatic}`}>
        <div className={Styles.header}>
          <Text variant="placeholder" color="var(--dark-40)">
            Campaign
          </Text>
          {canChangeCampaign && (
            <Button size="sm" variant="secondary" onPress={handleChange}>
              Change
            </Button>
          )}
        </div>
        <div className={Styles.content}>
          <Text variant="secondary" lineClamp={1}>
            One-off content
          </Text>
          <Text variant="metadata" color="var(--dark-60)">
            Post not assigned to a campaign
          </Text>
        </div>
      </div>
    )
  }

  const campaignPath = generatePath(BRAND_PLAN_SCHEDULE_PATH, {
    workspaceId,
    scheduleId: campaignInfo.id,
  })
  const strategyLabel = getStrategyLabel(campaignInfo.content_strategy_type)

  return (
    <Link to={campaignPath} className={Styles.card} onClick={() => onClose?.()}>
      <Text variant="placeholder" color="var(--dark-40)">
        Campaign
      </Text>
      <div className={Styles.content}>
        <Text variant="secondary" lineClamp={2}>
          {campaignInfo.title}
        </Text>
        {strategyLabel && (
          <Text variant="metadata" color="var(--dark-60)">
            {strategyLabel}
          </Text>
        )}
      </div>
    </Link>
  )
}
